import React, {Fragment, useEffect, useState} from 'react'
import I18n from 'i18n-js'
import yup from '../../../lib/YupValidations'
import {saveAs} from 'file-saver'
import {Auth} from 'aws-amplify';

import Form from '../../shared/Form'
import Button from '../../shared/Button'
import * as PropTypes from 'prop-types'
import styled from 'styled-components'
import colors from '../../../lib/Colors'
import fonts from '../../../lib/Fonts'
import CreateRetailerForm from '../CreateRetailerForm'
import titleize from '../../../util/titleize'
import ExternalLink from '../../shared/ExternalLink'
import ServerErrorMessage from '../../shared/Form/ServerErrorMessage';

const formFields = [
  {
    component: 'input',
    id: 'firstName',
    label: I18n.t('shared.firstName'),
    name: 'firstName',
    validation: yup
      .string()
      .required()
      .trim()
      .max(40, 'First name is too long.'),
    size: 2,
    isFirstInRow: true
  },
  {
    component: 'input',
    id: 'lastName',
    label: I18n.t('shared.lastName'),
    name: 'lastName',
    validation: yup
      .string()
      .required()
      .trim()
      .max(256, 'Last name is too long.'),
    size: 2,
    isLastInRow: true,
  },
  {
    id: 'email',
    label: I18n.t('shared.workEmailAddress'),
    name: 'email',
    type: 'email',
    validation: yup
      .string()
      .required()
      .email()
      .max(256, 'Email is too long.'),
  },
  {
    id: 'phoneNumber',
    label: I18n.t('shared.mobilePhone'),
    name: 'phoneNumber',
    validation: yup.string().required(),
  },
  {
    id: 'password',
    label: I18n.t('shared.password'),
    name: 'password',
    type: 'password',
    customValidation: true,
    passwordHelper: true,
    validation: yup
      .string()
      .required('Password is required.')
      .min(6, I18n.t('errors.passwordFieldError'))
      .max(40, 'Password is too long.')
      .matches(/(?=.*[A-Z])/, I18n.t('errors.passwordFieldError'))
      .matches(/(?=.*[a-z])/, I18n.t('errors.passwordFieldError'))
      .trim(),
  }
]

const FormHeader = styled.h2`
  font-size: 1.75em;
  color: ${colors.almostBlack};
  font-family: ${fonts.family.workSans};
  margin: 0 0 1em;
  font-weight: 300;
`

const InviteText = styled.p`
  color: ${colors.darkerGray};
  font-family: ${fonts.family.workSans};
  margin: 0 0 1em;
  font-weight: 300;
  cursor: default;
`

const Copy = styled.p`
  color: ${colors.darkerGray};
  font-family: ${fonts.family.workSans};
  margin: 1em 0 0;
  font-weight: 300;
  cursor: default;
`

const TermsOfServiceLink = styled(ExternalLink)`
  display: inline;
`

const StyledCreateButtonWrapper = styled.div`
  margin-top: 11px;
`

const CreateUserForm = ({
                          createAccount,
                          createRetailer,
                          isLoading,
                          isCreateAccountFulfilled,
                          establishmentName,
                          onrampType,
                          errorMessage,
                          emailError,
                          phoneError,
                          resetState,
                          skipRetailer,
                          hideFormHeader,
                          downloadableUrl,
                          redirectUrl,
                          email,
                          ...rest
                        }) => {
  const [userData, setUserData] = useState(null);
  useEffect(() => {
    //Resetting state on mount of signUp component
    resetState();
  }, []);

  useEffect(() => {
    const initiate = async () => {
      const {idToken} = await Auth.currentSession();
      const {payload, jwtToken} = idToken;
      const {given_name, family_name, phone_number, email} = payload;
      const user_id = payload['cognito:username'];
      const user = {
        user_first_name: given_name,
        user_last_name: family_name,
        user_phone: phone_number,
        user_email: email,
        user_id,
        token: jwtToken || ''
      };
      setUserData(user);
    };
    initiate().catch(error => console.error(error));
  }, []);

  formFields[2]['initialValue'] = email;

  return (
    <Fragment>
      {((!isCreateAccountFulfilled && !userData) || skipRetailer) && (
        <Fragment>
          {!hideFormHeader && <FormHeader>{I18n.t('auth.createYourAccount')}</FormHeader>}
          {establishmentName && (
            <InviteText>
              {I18n.t('auth.invitedToJoinEstablishment', {
                name: titleize(establishmentName),
              })}
            </InviteText>
          )}
          <Form
            emailError={emailError}
            errorMessage={errorMessage}
            fields={formFields}
            onSubmit={(...args) => {
              if (downloadableUrl) {
                saveAs(downloadableUrl)
              }
              createAccount({...args, skipRetailer, redirectUrl})
            }}
            phoneError={phoneError}
            {...rest}
          >
            {errorMessage && (<ServerErrorMessage text={errorMessage}/>)}
            <StyledCreateButtonWrapper>
              <Button
                disabled={isLoading}
                isLoading={isLoading}
                size={'full'}
                type={'submit'}
              >
                {I18n.t('auth.getStarted')}
              </Button>
            </StyledCreateButtonWrapper>
            {!hideFormHeader && <Copy>
              {I18n.t('auth.agreeToTermsPartial')}
              <TermsOfServiceLink href={'https://www.bevspot.com/terms-conditions'}>
                {I18n.t('auth.termsLabel')}
              </TermsOfServiceLink>
            </Copy>}
          </Form>
        </Fragment>
      )}
      {((userData || isCreateAccountFulfilled) && !skipRetailer) &&
      <CreateRetailerForm onrampType={onrampType} redirectUrl={redirectUrl} userData={userData}/>}
    </Fragment>
  )
}

CreateUserForm.propTypes = {
  createAccount: PropTypes.func,
  createRetailer: PropTypes.bool,
  downloadableUrl: PropTypes.string,
  emailError: PropTypes.bool,
  errorMessage: PropTypes.string,
  establishmentName: PropTypes.any,
  hideFormHeader: PropTypes.bool,
  isCreateAccountFulfilled: PropTypes.bool,
  isLoading: PropTypes.bool,
  onrampType: PropTypes.string,
  phoneError: PropTypes.bool,
  redirectUrl: PropTypes.string,
  resetState: PropTypes.func,
  skipRetailer: PropTypes.bool
}

CreateUserForm.defaultProps = {
  skipRetailer: false,
  hideFormHeader: false,
}

export default CreateUserForm
